import { CircularProgress } from '@mui/joy';
import { Divider, Fade, Modal, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import CustomerAccessComponent, {
  CustomerListItemPatch,
} from './customerAccessComponent';
import EditAccessReportComponent from './editAccessReportComponent';
import FinalReportTemplateComponent from './finalReportTemplateComponent';
import LimitViewReportComponent from './limitViewReportComponent';
import RemovalRightsComponent from './removalRightsComponent';
import ReopenReportComponent from './reopenReportComponent';
import ReportAccessComponent from './reportAccessComponent';
import ReportCategoriesComponent, {
  ReportTypeData,
} from './reportCategoriesComponent';
import ReportsDownloadComponent from './reportsDownloadComponent';
import ViewReportOnlyComponent from './viewReportOnlyComponent';
import WorkflowAccessComponent from './workflowAccessComponent';

import { ROLE_VALUES } from '../../constants/customerConstant';
import { UserRoleModalProps } from '../../interfaces/ModalProps';

import { useIntakeChannel } from '~/slices/intakeChannelSlice';

interface AccessRoleModalProps extends UserRoleModalProps {
  slots: {
    extraDetails: (userId: number) => React.ReactElement;
  };
}

const AccessRoleModal = ({
  open,
  handleClose,
  userName,
  userId,
  slots,
  userRole,
}: AccessRoleModalProps) => {
  const slug = localStorage.getItem('slug');
  const channelId = useIntakeChannel().intakeChannelData.selectedChannel ?? '';
  const [allowedReportTypes, setAllowedReportTypes] = useState<
    ReportTypeData[]
  >([]);
  const [allowedChannels, setAllowedChannels] = useState<
    { id: number; name: string }[]
  >([]);
  const [allowedCustomerAccess, setAllCustomerAccess] = useState<
    CustomerListItemPatch[]
  >([]);
  const [viewReportAssigned, setViewReportAssigned] = useState(false);
  const [viewReportDepartment, setViewReportDepartment] = useState(false);
  const [readReportOnly, setReadReportOnly] = useState(false);
  const [allowedToSendMessages, setAllowedToSendMessages] = useState(false);
  const [allowedToGenerateFinalReport, setAllowedToGenerateFinalReport] =
    useState(false);
  const [
    allowedToRequestForAdditionalInformation,
    setAllowedToRequestForAdditionalInformation,
  ] = useState(false);
  const [allowedToReopen, setAllowedToReopen] = useState(false);
  const [isTemplateAllowed, setIsTemplateAllowed] = useState(false);
  const [isAllowedToRemovePII, setIsAllowedToRemovePII] = useState(false);
  const [isAllowedToRedactReport, setIsAllowedToRedactReport] = useState(false);
  const [isAllowedToDownloadFinalReport, setIsAllowedToDownloadFinalReport] =
    useState(false);
  const [isAllowedToDownloadAttachment, setIsAllowedToDownloadAttachment] =
    useState(false);
  const [allowedReportIds, setAllowedReportIds] = useState<string[]>([]);
  const [restrictedReportIds, setRestrictedReportIds] = useState<string[]>([]);

  const { data, isLoading, isValidating, error, mutate } = useSWR(
    slug && userId
      ? `tenant/${slug}/users/${userId}/role-settings?channel_id=${channelId}`
      : '',
    { revalidateOnFocus: false },
  );
  useEffect(() => {
    if (!data) return;
    const userApiFormattedData = data?.allowed_report_types?.map(
      ({ id, name }: ReportTypeData) => ({ id, name }),
    );
    setAllowedReportIds(data?.allowed_report_access);
    setRestrictedReportIds(data?.restrict_report_access);
    setAllowedReportTypes(userApiFormattedData);
    const cusomerAccess = data?.allowed_tenants;
    setAllCustomerAccess(cusomerAccess ?? []);
    setViewReportAssigned(data?.is_only_allowed_to_view_assigned_report);
    setViewReportDepartment(data?.is_only_allowed_to_view_report_in_department);
    setReadReportOnly(data?.is_read_only_report);
    setAllowedToSendMessages(data?.is_allowed_to_send_messages);
    setAllowedToGenerateFinalReport(data?.is_allowed_to_generate_final_report);
    setAllowedToRequestForAdditionalInformation(
      data?.is_allowed_to_request_for_additional_information,
    );
    setAllowedToReopen(data?.is_allowed_to_reopen_report);
    setIsTemplateAllowed(data?.is_allowed_to_create_final_report_template);
    setIsAllowedToRemovePII(data?.is_allowed_to_request_pii_removal);
    setIsAllowedToRedactReport(data?.is_allowed_to_redact_report);
    setIsAllowedToDownloadFinalReport(
      data?.is_allowed_to_download_final_report,
    );
    setIsAllowedToDownloadAttachment(data?.is_allowed_to_download_attachments);
    setAllowedChannels(data.allowed_form_type_ids ?? []);
  }, [data]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        style={{
          border: 'none',
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '860px',
              maxHeight: '680px',
              bgcolor: 'rgba(255, 255, 255, 1)',
              borderRadius: '6px',
              boxShadow: 24,
              p: '32px',
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '22px',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              {userName}
            </Typography>
            {(userRole === 7 || userRole === 6) && (
              <CustomerAccessComponent
                userId={userId}
                selectedCustomerList={allowedCustomerAccess}
                loading={isLoading}
                error={error}
                mutate={mutate}
              />
            )}
            {slots.extraDetails(userId)}
            <Divider
              sx={{
                borderColor: '#D1D1D1',
                borderBottomWidth: '1px',
                width: '752px',
              }}
            />
            <ReportCategoriesComponent
              userId={userId}
              allowedReportCategories={allowedReportTypes}
              loading={isLoading}
              isError={error}
              mutate={mutate}
            />
            <Divider
              sx={{
                borderColor: '#D1D1D1',
                borderBottomWidth: '1px',
                width: '752px',
                my: '20px',
              }}
            />
            {!isValidating ? (
              <WorkflowAccessComponent
                user_id={userId}
                allowedChannel={allowedChannels.map((channel) => channel.id)}
                mutate={mutate}
              />
            ) : (
              <CircularProgress color='neutral' size='sm' />
            )}
            <Divider
              sx={{
                borderColor: '#D1D1D1',
                borderBottomWidth: '1px',
                width: '752px',
                mt: '20px',
              }}
            />
            <ReportAccessComponent
              ReportIds={allowedReportIds}
              userId={userId}
              loading={isLoading}
              isError={error}
              mutate={mutate}
              type='allow'
            />
            <ReportAccessComponent
              ReportIds={restrictedReportIds}
              userId={userId}
              loading={isLoading}
              isError={error}
              mutate={mutate}
              type='restrict'
            />
            <LimitViewReportComponent
              userId={userId}
              switchOne={viewReportAssigned}
              switchTwo={viewReportDepartment}
              isError={error}
              mutate={mutate}
            />
            <ViewReportOnlyComponent
              userId={userId}
              readReportOnly={readReportOnly}
              isError={error}
              mutate={mutate}
            />
            {userRole === ROLE_VALUES.TENANT_USER && (
              <EditAccessReportComponent
                userId={userId}
                allowedToSendMessages={allowedToSendMessages}
                allowedToGenerateFinalReport={allowedToGenerateFinalReport}
                allowedToRequestForAdditionalInformation={
                  allowedToRequestForAdditionalInformation
                }
                isError={error}
                mutate={mutate}
              />
            )}
            <ReopenReportComponent
              userId={userId}
              allowedToReopen={allowedToReopen}
              isError={error}
              mutate={mutate}
            />
            <FinalReportTemplateComponent
              userId={userId}
              isTemplateAllowed={isTemplateAllowed}
              isError={error}
              mutate={mutate}
            />

            <RemovalRightsComponent
              userId={userId}
              allowedToRemovePII={isAllowedToRemovePII}
              allowedToRedactReport={isAllowedToRedactReport}
              isError={error}
              mutate={mutate}
            />

            <ReportsDownloadComponent
              userId={userId}
              isAllowedToDownloadAttachment={isAllowedToDownloadAttachment}
              isAllowedToDownloadFinalReport={isAllowedToDownloadFinalReport}
              isError={error}
              mutate={mutate}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default AccessRoleModal;
